import React, { useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import {
  append,
  mergeDeepRight,
  mergeRight,
  propEq,
  reject,
  remove,
} from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import EditIcon from "@material-ui/icons/Edit";
import ContributorRolesDialog from "../edit/ContributorRolesDialog";
import { caseInsensitiveSort } from "../../util/sorts";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const sortContributorRoles = caseInsensitiveSort("display");

const ContributorRolesCard = ({
  submissionType,
  project,
  loading,
  onUpdate,
}) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const updatedSubType = mergeRight(submissionType, {
        contributorRoles: data,
      });
      const update = mergeDeepRight(project, {
        submissionTypes: append(
          updatedSubType,
          reject(propEq("name", submissionType.name), project.submissionTypes),
        ),
      });
      console.log(update);
      onUpdate(update);
    },
    [submissionType, project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const updatedSubType = mergeRight(submissionType, {
        contributorRoles: remove(
          idx,
          1,
          sortContributorRoles(submissionType.contributorRoles),
        ),
      });
      const update = mergeDeepRight(project, {
        submissionTypes: append(
          updatedSubType,
          reject(propEq("name", submissionType.name), project.submissionTypes),
        ),
      });
      console.log(update);
      onUpdate(update);
    },
    [project, submissionType, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
          color: "primary",
        }}
        title={`${submissionType.displayName} Contributor Roles`}
        action={
          <Tooltip title="Add Contributor Roles">
            <IconButton onClick={handleAddClick}>
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(submissionType) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Contributor Roles.
          </Box>
        )}
        {isBlank(submissionType.contributorRoles) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Contributor Roles set up for this Submission Type.
          </Box>
        )}
        {notBlank(submissionType.contributorRoles) && (
          <List dense style={{ maxHeight: 160, overflowY: "auto" }}>
            {mapIndexed(
              (role, idx) => (
                <ListItem key={role.name}>
                  <ListItemText primary={role.display} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(idx)}
                      dialogTitle="Delete Contributor Role"
                      dialogBody="Are you sure you want to remove this contributor role?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              sortContributorRoles(submissionType.contributorRoles),
            )}
          </List>
        )}
      </CardContent>

      {dialogOpen && (
        <ContributorRolesDialog
          submissionType={submissionType}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default ContributorRolesCard;
